// src/components/support/SupportChat.jsx
import { useState, useEffect, useRef, useCallback } from "react";
import { T } from "../ui";
import { sendMessage, subscribeToMessages, markChatAsRead } from "../../services/supportService";
import { useAuth } from "../../contexts/AuthContext";

function fmtTime(ts) {
  if (!ts) return "";
  try {
    const d = ts.toDate ? ts.toDate() : new Date((ts.seconds || 0) * 1000);
    return d.toLocaleTimeString("ar-EG", { hour: "2-digit", minute: "2-digit" });
  } catch { return ""; }
}

// ── Message bubble ─────────────────────────────────────────────
function Bubble({ msg, isMe }) {
  return (
    <div style={{ display: "flex", justifyContent: isMe ? "flex-start" : "flex-end" }}>
      <div style={{
        maxWidth: "78%", padding: "9px 13px",
        borderRadius: isMe ? "12px 12px 12px 4px" : "12px 12px 4px 12px",
        background: isMe ? "white" : "linear-gradient(135deg,#F97316,#EA580C)",
        color: isMe ? T.text : "white",
        fontSize: 13, lineHeight: 1.5,
        border: isMe ? `1px solid ${T.border}` : "none",
        boxShadow: "0 1px 3px rgba(0,0,0,.06)",
      }}>
        <div>{msg.text}</div>
        <div style={{ fontSize: 10, opacity: 0.5, marginTop: 3, textAlign: isMe ? "left" : "right" }}>
          {fmtTime(msg.createdAt)}
        </div>
      </div>
    </div>
  );
}

// ── CLIENT chat ────────────────────────────────────────────────
export function ClientSupportChat({ settings }) {
  const { user } = useAuth();
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const bottomRef = useRef(null);

  useEffect(() => {
    if (!user?.id) return;
    const unsub = subscribeToMessages(
      user.id,
      d => { setMsgs(d); setLoading(false); },
      () => setLoading(false)
    );
    return unsub;
  }, [user?.id]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [msgs]);

  const send = useCallback(async () => {
    const t = text.trim();
    if (!t || sending || !user) return;
    setText("");
    setSending(true);
    try { await sendMessage(user.id, user.name, t, "client", user.name); }
    catch (e) { console.error(e); setText(t); }
    setSending(false);
  }, [text, sending, user]);

  return (
    <div style={{
      display: "flex",
      flexDirection: "column",
      borderRadius: 16,
      overflow: "hidden",
      background: "white",
      boxShadow: "0 10px 25px -5px rgba(0,0,0,0.1)",
      border: `1px solid ${T.border}`
    }}>
      {/* الاحترافية الجديدة في مكان الاتصال والهيدر */}
      <div style={{
        display: "flex",
        alignItems: "center",
        padding: "14px 18px",
        background: "white",
        borderBottom: `1px solid ${T.border}`,
        gap: 12
      }}>
        <div style={{
          width: 40, height: 40, borderRadius: "50%",
          background: "linear-gradient(135deg,#F97316,#EA580C)",
          display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: 20, boxShadow: "0 2px 8px rgba(249,115,22,.3)"
        }}>🚗</div>

        <div style={{ flex: 1 }}>
          <div style={{ color: T.text, fontWeight: 800, fontSize: 14 }}>دعم رايدو</div>
          <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
            <span style={{ width: 8, height: 8, borderRadius: "50%", background: "#10B981" }} />
            <span style={{ color: "#64748B", fontSize: 11, fontWeight: 500 }}>متاح للرد الآن</span>
          </div>
        </div>

        {settings?.supportPhone && (
          <a href={`tel:${settings.supportPhone}`}
            title="اتصال سريع"
            style={{
              width: 38, height: 38, borderRadius: "10px",
              background: "#F0FDF4", color: "#16A34A",
              display: "flex", alignItems: "center", justifyContent: "center",
              textDecoration: "none", border: "1px solid #DCFCE7",
              transition: "all 0.2s"
            }}>
            <span style={{ fontSize: 18 }}>📞</span>
          </a>
        )}
      </div>

      <div style={{ minHeight: 280, maxHeight: 380, overflowY: "auto", padding: 14, background: "#F8FAFC", display: "flex", flexDirection: "column", gap: 8 }}>
        {loading && <div style={{ textAlign: "center", color: T.muted, fontSize: 13, padding: 20 }}>جاري التحميل...</div>}
        {!loading && msgs.length === 0 && (
          <div style={{ textAlign: "center", color: T.muted, fontSize: 14, padding: "30px 0" }}>
            <div style={{ fontSize: 40, marginBottom: 10 }}>👋</div>
            أهلاً! كيف يمكننا مساعدتك؟
          </div>
        )}
        {msgs.map(m => <Bubble key={m.id} msg={m} isMe={m.senderRole === "client"} />)}
        <div ref={bottomRef} />
      </div>

      <div style={{ display: "flex", gap: 8, padding: "10px 12px", background: "white", borderTop: `1px solid ${T.border}` }}>
        <input
          value={text} onChange={e => setText(e.target.value)}
          onKeyDown={e => e.key === "Enter" && !e.shiftKey && (e.preventDefault(), send())}
          placeholder="اكتب رسالتك..."
          style={{ flex: 1, padding: "10px 14px", borderRadius: 10, border: `1px solid ${T.border}`, fontFamily: "inherit", fontSize: 14, outline: "none", background: "#F1F5F9" }}
        />
        <button onClick={send} disabled={!text.trim() || sending}
          style={{ width: 42, height: 42, borderRadius: 10, border: "none", cursor: !text.trim() ? "not-allowed" : "pointer", background: "linear-gradient(135deg,#F97316,#EA580C)", color: "white", fontSize: 20, display: "flex", alignItems: "center", justifyContent: "center", opacity: !text.trim() ? 0.45 : 1, flexShrink: 0 }}>
          ↑
        </button>
      </div>
    </div>
  );
}

// ── ADMIN support panel ────────────────────────────────────────
export function AdminSupportPanel({ chats }) {
  const { user } = useAuth();
  const [selId, setSelId] = useState(null);
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [loadingMsg, setLoadingMsg] = useState(false);
  const bottomRef = useRef(null);

  const selChat = chats.find(c => c.id === selId);

  useEffect(() => {
    if (!selId) return;
    setLoadingMsg(true);
    setMsgs([]);
    const unsub = subscribeToMessages(selId,
      d => { setMsgs(d); setLoadingMsg(false); },
      () => setLoadingMsg(false)
    );
    markChatAsRead(selId).catch(() => { });
    return unsub;
  }, [selId]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [msgs]);

  const send = useCallback(async () => {
    const t = text.trim();
    if (!t || sending || !selId || !selChat || !user) return;
    setText("");
    setSending(true);
    try { await sendMessage(selId, selChat.userName, t, "admin", user.name); }
    catch (e) { console.error(e); setText(t); }
    setSending(false);
  }, [text, sending, selId, selChat, user]);

  return (
    <div style={{ display: "flex", gap: 12, height: 520, minHeight: 0 }}>
      {/* Conversations list */}
      <div style={{ width: 200, flexShrink: 0, background: "white", borderRadius: 14, border: `1px solid ${T.border}`, display: "flex", flexDirection: "column", overflow: "hidden" }}>
        <div style={{ padding: "12px 14px", borderBottom: `1px solid ${T.border}`, fontWeight: 700, fontSize: 13, color: T.muted, flexShrink: 0 }}>
          المحادثات ({chats.length})
        </div>
        <div style={{ flex: 1, overflowY: "auto" }}>
          {chats.length === 0 && <div style={{ padding: 20, textAlign: "center", color: T.muted, fontSize: 13 }}>لا توجد محادثات</div>}
          {chats.map(chat => (
            <button key={chat.id} onClick={() => setSelId(chat.id)} style={{
              width: "100%", padding: "10px 12px", border: "none",
              background: selId === chat.id ? "#EFF6FF" : "transparent",
              cursor: "pointer", fontFamily: "inherit", textAlign: "right",
              borderRight: `3px solid ${selId === chat.id ? T.info : "transparent"}`,
              transition: "all .15s",
            }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 4 }}>
                <span style={{ fontWeight: 700, fontSize: 13, flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{chat.userName}</span>
                {chat.adminUnread > 0 && (
                  <span style={{ background: T.danger, color: "white", borderRadius: 10, minWidth: 18, height: 18, fontSize: 10, fontWeight: 800, display: "flex", alignItems: "center", justifyContent: "center", padding: "0 4px", flexShrink: 0 }}>{chat.adminUnread}</span>
                )}
              </div>
              <div style={{ fontSize: 11, color: T.muted, marginTop: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {chat.lastSender === "admin" ? "أنت: " : ""}{chat.lastMessage}
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Chat area */}
      <div style={{ flex: 1, display: "flex", flexDirection: "column", background: "white", borderRadius: 14, border: `1px solid ${T.border}`, overflow: "hidden", minWidth: 0 }}>
        {!selId ? (
          <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", color: T.muted }}>
            <div style={{ fontSize: 48, marginBottom: 12 }}>💬</div>
            <p style={{ fontSize: 14 }}>اختر محادثة من القائمة</p>
          </div>
        ) : (
          <>
            <div style={{ padding: "12px 16px", borderBottom: `1px solid ${T.border}`, display: "flex", alignItems: "center", gap: 10, background: "#F8FAFC", flexShrink: 0 }}>
              <div style={{ width: 34, height: 34, borderRadius: "50%", background: `linear-gradient(135deg,${T.success},#059669)`, display: "flex", alignItems: "center", justifyContent: "center", color: "white", fontWeight: 800, fontSize: 14 }}>
                {selChat?.userName?.charAt(0) || "؟"}
              </div>
              <div>
                <div style={{ fontWeight: 700, fontSize: 14 }}>{selChat?.userName}</div>
                <div style={{ fontSize: 11, color: T.muted }}>عميل</div>
              </div>
            </div>
            <div style={{ flex: 1, overflowY: "auto", padding: "12px 14px", background: "#F8FAFC", display: "flex", flexDirection: "column", gap: 8 }}>
              {loadingMsg && <div style={{ textAlign: "center", color: T.muted, fontSize: 13 }}>جاري التحميل...</div>}
              {msgs.map(m => <Bubble key={m.id} msg={m} isMe={m.senderRole === "admin"} />)}
              <div ref={bottomRef} />
            </div>
            <div style={{ display: "flex", gap: 8, padding: "10px 12px", borderTop: `1px solid ${T.border}`, background: "white", flexShrink: 0 }}>
              <input value={text} onChange={e => setText(e.target.value)}
                onKeyDown={e => e.key === "Enter" && !e.shiftKey && (e.preventDefault(), send())}
                placeholder="رد على العميل..."
                style={{ flex: 1, padding: "10px 14px", borderRadius: 10, border: `1px solid ${T.border}`, fontFamily: "inherit", fontSize: 14, outline: "none", background: "#F1F5F9" }}
              />
              <button onClick={send} disabled={!text.trim() || sending}
                style={{ width: 42, height: 42, borderRadius: 10, border: "none", cursor: !text.trim() ? "not-allowed" : "pointer", background: "linear-gradient(135deg,#7C3AED,#5B21B6)", color: "white", fontSize: 20, display: "flex", alignItems: "center", justifyContent: "center", opacity: !text.trim() ? 0.45 : 1, flexShrink: 0 }}>
                ↑
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}